import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Package, CheckCircle2, Truck, Clock, AlertCircle, ArrowLeft, CreditCard } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { formatFCFA } from '../lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: 'placed',    label: 'Commande reçue',   icon: Package },
  { key: 'paid',      label: 'Paiement confirmé', icon: CreditCard },
  { key: 'shipped',   label: 'Expédiée',          icon: Truck },
  { key: 'delivered', label: 'Livrée',            icon: CheckCircle2 },
];

function getCurrentStep(order) {
  const s = (order.status || '').toLowerCase();
  const pay = (order.payment_status || '').toLowerCase();
  if (s === 'cancelled') return 'cancelled';
  if (s === 'delivered' || s === 'livr\u00e9' || s === 'livr\u00e9e') return 'delivered';
  if (s === 'shipped' || s === 'exp\u00e9di\u00e9' || s === 'exp\u00e9di\u00e9e') return 'shipped';
  if (s === 'processing' || s === 'en pr\u00e9paration') return 'paid';
  if (pay === 'paid' || order.payment_method === 'cod') return order.payment_method === 'cod' ? 'placed' : 'paid';
  return 'placed';
}

export default function OrderTrackingPage() {
  const { orderId } = useParams();
  const { customer } = useCustomerAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}/public`);
        if (!cancelled) {
          setOrder(res.data);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError("Commande introuvable.");
          setLoading(false);
        }
      }
    };
    fetch();
    const t = setInterval(fetch, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [orderId]);

  if (loading) {
    return (
      <div className="pt-28 pb-24 min-h-screen flex items-center justify-center">
        <p className="text-black/40 text-sm">Chargement...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="pt-28 pb-24 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle size={48} className="mx-auto mb-6" style={{ color: '#B80000' }} />
          <h1 className="font-serif text-3xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Commande introuvable</h1>
          <p className="text-sm text-black/50 mb-8">{error || "Aucune commande ne correspond à cet identifiant."}</p>
          <Link to="/account" className="btn-secondary inline-block">Mes Commandes</Link>
        </div>
      </div>
    );
  }

  const currentStep = getCurrentStep(order);
  const isCancelled = currentStep === 'cancelled';
  const currentIndex = STEPS.findIndex(s => s.key === currentStep);

  return (
    <div data-testid="order-tracking-page" className="pt-24 sm:pt-28 pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to={customer ? "/account" : "/"} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-black/40 hover:text-black transition-colors mb-8">
          <ArrowLeft size={14} /> Retour
        </Link>

        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 mb-2">Commande #{(order.order_id || '').replace('ord_', '').toUpperCase()}</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {isCancelled ? 'Commande annulée' : 'Suivi de votre commande'}
          </h1>
        </div>

        {/* Timeline */}
        {!isCancelled ? (
          <div className="border border-black/10 p-8 mb-8">
            <div className="relative">
              {STEPS.map((step, i) => {
                const Icon = step.icon;
                const reached = i <= currentIndex;
                const isCurrent = i === currentIndex;
                return (
                  <div key={step.key} data-testid={`timeline-step-${step.key}`} className="flex gap-5 pb-8 last:pb-0 relative">
                    {i < STEPS.length - 1 && (
                      <div
                        className="absolute left-5 top-12 bottom-0 w-px"
                        style={{ backgroundColor: i < currentIndex ? '#B80000' : 'rgba(0,0,0,0.1)' }}
                      />
                    )}
                    <div
                      className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full border-2 relative z-10"
                      style={{
                        backgroundColor: reached ? '#B80000' : '#F5F0E8',
                        borderColor: reached ? '#B80000' : 'rgba(0,0,0,0.15)',
                        color: reached ? '#fff' : 'rgba(0,0,0,0.3)'
                      }}
                    >
                      <Icon size={16} strokeWidth={reached ? 2.5 : 1.5} />
                    </div>
                    <div className="flex-1 pt-1.5">
                      <div className="flex items-center gap-3">
                        <p className={`text-sm font-semibold ${reached ? 'text-black' : 'text-black/30'}`}>{step.label}</p>
                        {isCurrent && (
                          <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5" style={{ backgroundColor: '#B80000', color: '#fff' }}>
                            En cours
                          </span>
                        )}
                      </div>
                      {isCurrent && step.key === 'placed' && order.payment_method === 'cod' && (
                        <p className="text-xs text-black/40 mt-1">Nous préparons votre colis. Règlement à la livraison.</p>
                      )}
                      {isCurrent && step.key === 'paid' && (
                        <p className="text-xs text-black/40 mt-1">Votre paiement est confirmé. Préparation en cours.</p>
                      )}
                      {isCurrent && step.key === 'shipped' && (
                        <p className="text-xs text-black/40 mt-1">Votre colis est en route.</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="border border-black/10 p-8 mb-8 text-center">
            <Clock size={40} className="mx-auto mb-4" style={{ color: '#B80000' }} />
            <p className="text-sm text-black/60">Cette commande a été annulée.</p>
          </div>
        )}

        {/* Summary */}
        <div className="border border-black/10 p-8">
          {order.tracking_number && (
            <div className="mb-6 p-4 border-l-2" style={{ borderColor: '#B80000', backgroundColor: 'rgba(184,0,0,0.04)' }}>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/40">Numéro de suivi</p>
              <p className="text-lg font-mono tracking-wider mt-1">{order.tracking_number}</p>
            </div>
          )}
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/40">Total</p>
              <p className="text-2xl font-light mt-1">{formatFCFA(order.total)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/40">Client</p>
              <p className="text-sm mt-1">{order.customer_name}</p>
              <p className="text-xs text-black/40">{order.customer_email}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
